// src/pages/MessageComposer.js - Full-page message composer for rules and custom messages
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MessageIcon from '@mui/icons-material/Message';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { eventService, customMessageService, alertRuleService } from '../services/api';
import './MessageComposer.css';

const MessageComposer = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Message composition state
  const [messageType, setMessageType] = useState('custom'); // 'custom' or 'rule'
  const [messageData, setMessageData] = useState({
    message: '',
    recipientFilter: 'all',
    scheduleOption: 'immediate',
    scheduledDate: '',
    scheduledTime: '',
    ruleType: 'rsvp',
    triggerField: 'rsvp',
    triggerValue: 'yes'
  });

  // Media state
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploadError, setUploadError] = useState('');

  const loadEventData = useCallback(async () => {
    try {
      setLoading(true);
      const eventData = await eventService.getEventDetails(eventId);
      setEvent(eventData);
      setGuests(eventData.guests || []);
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadEventData();
  }, [loadEventData]);

  // Filter guests based on selected criteria
  const getFilteredGuests = () => {
    const validGuests = guests.filter(guest => guest.whatsappNumber?.trim());

    if (messageType === 'rule') {
      // For rules, filter based on trigger condition
      if (messageData.triggerField === 'rsvp') {
        return validGuests.filter(g => g.rsvp === messageData.triggerValue);
      }
      return validGuests;
    }

    // For custom messages
    switch (messageData.recipientFilter) {
      case 'confirmed':
        return validGuests.filter(g => g.rsvp === 'yes');
      case 'pending':
        return validGuests.filter(g => g.rsvp === 'pending');
      case 'declined':
        return validGuests.filter(g => g.rsvp === 'no');
      case 'notinvited':
        return validGuests.filter(g => g.rsvp === 'unassigned');
      case 'all':
      default:
        return validGuests;
    }
  };

  const filteredGuests = getFilteredGuests();

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError('');
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

    // WhatsApp limits
    const maxSizes = {
      'image': 5,
      'video': 16,
      'document': 100
    };

    let mediaType = 'document';
    if (file.type.startsWith('image/')) mediaType = 'image';
    else if (file.type.startsWith('video/')) mediaType = 'video';

    if (file.size > maxSizes[mediaType] * 1024 * 1024) {
      setUploadError(`File too large. ${mediaType} files must be under ${maxSizes[mediaType]}MB`);
      return;
    }

    setMediaFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setMediaPreview({
          type: 'image',
          url: e.target.result,
          name: file.name,
          size: fileSizeMB
        });
      };
      reader.readAsDataURL(file);
    } else {
      setMediaPreview({
        type: file.type.startsWith('video/') ? 'video' : 'document',
        name: file.name,
        size: fileSizeMB
      });
    }
  };

  // Upload media to R2
  const uploadMediaFile = async () => {
    if (!mediaFile) return null;

    const formData = new FormData();
    formData.append('file', mediaFile);
    formData.append('eventId', eventId);

    try {
      setUploadProgress(30);
      const response = await fetch('/api/media/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      setUploadProgress(80);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      setUploadProgress(100);
      return result.media.url;
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message);
      return null;
    } finally {
      setUploadProgress(0);
    }
  };

  // Send message or create rule
  const handleSend = async () => {
    if (!messageData.message.trim() && !mediaFile) {
      alert('Please enter a message or attach media');
      return;
    }

    if (filteredGuests.length === 0) {
      alert('No recipients match your criteria');
      return;
    }

    setSending(true);
    try {
      let uploadedMediaUrl = null;

      // Upload media if present
      if (mediaFile) {
        uploadedMediaUrl = await uploadMediaFile();
        if (!uploadedMediaUrl && mediaFile) {
          alert('Failed to upload media');
          setSending(false);
          return;
        }
      }

      if (messageType === 'custom') {
        // Send custom message
        const recipients = filteredGuests.map(guest => ({
          guestId: guest._id,
          name: guest.name,
          whatsappNumber: guest.whatsappNumber
        }));

        await customMessageService.sendCustomMessage(eventId, {
          message: messageData.message,
          mediaUrl: uploadedMediaUrl,
          recipients,
          recipientFilter: messageData.recipientFilter,
          scheduleOption: messageData.scheduleOption,
          scheduledDate: messageData.scheduledDate,
          scheduledTime: messageData.scheduledTime
        });

        alert(`Message queued for ${recipients.length} recipients`);
      } else {
        // Create auto-message rule
        await alertRuleService.createAlertRule(eventId, {
          ruleType: messageData.ruleType,
          triggerField: messageData.triggerField,
          triggerValue: messageData.triggerValue,
          messageTemplate: messageData.message,
          schedule: messageData.scheduleOption,
          timeOfDay: messageData.scheduledTime,
          isActive: true,
          mediaUrl: uploadedMediaUrl
        });

        alert('Auto-message rule created successfully');
      }

      // Navigate back to event details
      navigate(`/events/${eventId}`);
    } catch (error) {
      console.error('Error sending:', error);
      alert('Failed to send message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="message-composer-loading">Loading event data...</div>;
  }

  return (
    <div className="message-composer-container">
      {/* Header */}
      <div className="composer-header">
        <button className="back-button" onClick={() => navigate(`/events/${eventId}`)}>
          <ArrowBackIcon />
        </button>
        <div className="header-info">
          <h1>Message Composer</h1>
          <p>{event?.name}</p>
        </div>
      </div>

      {/* Message Type Selector */}
      <div className="composer-type-selector">
        <button
          className={`type-btn ${messageType === 'custom' ? 'active' : ''}`}
          onClick={() => setMessageType('custom')}
        >
          <SendIcon /> Custom Message
        </button>
        <button
          className={`type-btn ${messageType === 'rule' ? 'active' : ''}`}
          onClick={() => setMessageType('rule')}
        >
          <ScheduleIcon /> Auto-Message Rule
        </button>
      </div>

      <div className="composer-body">
        {/* Left Panel - Recipients/Rules */}
        <div className="composer-panel left-panel">
          {messageType === 'custom' ? (
            <>
              <h3><GroupIcon /> Recipients</h3>
              <div className="filter-section">
                <label><FilterListIcon /> Filter Recipients</label>
                <select
                  value={messageData.recipientFilter}
                  onChange={(e) => setMessageData({...messageData, recipientFilter: e.target.value})}
                >
                  <option value="all">All Guests ({guests.filter(g => g.whatsappNumber).length})</option>
                  <option value="confirmed">Confirmed ({guests.filter(g => g.rsvp === 'yes' && g.whatsappNumber).length})</option>
                  <option value="pending">Pending ({guests.filter(g => g.rsvp === 'pending' && g.whatsappNumber).length})</option>
                  <option value="declined">Declined ({guests.filter(g => g.rsvp === 'no' && g.whatsappNumber).length})</option>
                  <option value="notinvited">Not Invited ({guests.filter(g => g.rsvp === 'unassigned' && g.whatsappNumber).length})</option>
                </select>
              </div>

              <div className="recipients-preview">
                <p className="recipient-count">{filteredGuests.length} recipients selected</p>
                <div className="recipient-list">
                  {filteredGuests.slice(0, 5).map(guest => (
                    <div key={guest._id} className="recipient-chip">
                      {guest.name} {guest.plusOnes > 0 && `+${guest.plusOnes}`}
                    </div>
                  ))}
                  {filteredGuests.length > 5 && (
                    <div className="recipient-chip more">
                      +{filteredGuests.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <h3><ScheduleIcon /> Rule Configuration</h3>
              <div className="rule-config">
                <label>Rule Type</label>
                <select
                  value={messageData.ruleType}
                  onChange={(e) => setMessageData({...messageData, ruleType: e.target.value})}
                >
                  <option value="rsvp">RSVP Status</option>
                  <option value="task">Task Assignment</option>
                  <option value="vendor">Vendor Assignment</option>
                </select>

                {messageData.ruleType === 'rsvp' && (
                  <>
                    <label>Trigger When RSVP Is</label>
                    <select
                      value={messageData.triggerValue}
                      onChange={(e) => setMessageData({...messageData, triggerValue: e.target.value})}
                    >
                      <option value="yes">Yes (Confirmed)</option>
                      <option value="no">No (Declined)</option>
                      <option value="maybe">Maybe</option>
                    </select>
                  </>
                )}

                <div className="affected-guests">
                  <p>This rule will apply to {filteredGuests.length} current guests</p>
                  <small>And any future guests matching this condition</small>
                </div>
              </div>
            </>
          )}

          {/* Schedule Options */}
          <div className="schedule-section">
            <h4>Delivery Schedule</h4>
            <select
              value={messageData.scheduleOption}
              onChange={(e) => setMessageData({...messageData, scheduleOption: e.target.value})}
            >
              <option value="immediate">Send Immediately</option>
              <option value="scheduled">Schedule for Later</option>
            </select>

            {messageData.scheduleOption === 'scheduled' && (
              <div className="schedule-inputs">
                <input
                  type="date"
                  value={messageData.scheduledDate}
                  onChange={(e) => setMessageData({...messageData, scheduledDate: e.target.value})}
                />
                <input
                  type="time"
                  value={messageData.scheduledTime}
                  onChange={(e) => setMessageData({...messageData, scheduledTime: e.target.value})}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Message Composition */}
        <div className="composer-panel right-panel">
          <h3><MessageIcon /> Compose Message</h3>

          {/* Message Input */}
          <div className="message-input-section">
            <textarea
              className="message-textarea"
              placeholder={messageType === 'custom' ?
                "Type your message here..." :
                "Type your auto-message template...\n\nVariables:\n{{guest_name}} - Guest's name\n{{event_name}} - Event name\n{{event_date}} - Event date"
              }
              value={messageData.message}
              onChange={(e) => setMessageData({...messageData, message: e.target.value})}
              rows={8}
            />
            <div className="char-count">
              {messageData.message.length}/1600 characters
            </div>
          </div>

          {/* Media Upload */}
          <div className="media-section">
            <h4>Attach Media (Optional)</h4>

            {!mediaPreview ? (
              <div className="media-upload-area">
                <input
                  type="file"
                  id="media-input"
                  accept="image/*,video/*,application/pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="media-input" className="upload-button">
                  <CloudUploadIcon />
                  <span>Choose File</span>
                </label>
                <p className="upload-hint">
                  Images (5MB), Videos (16MB), PDFs (100MB)
                </p>
                {uploadError && (
                  <p className="upload-error">{uploadError}</p>
                )}
              </div>
            ) : (
              <div className="media-preview">
                {mediaPreview.type === 'image' ? (
                  <img src={mediaPreview.url} alt="Preview" />
                ) : (
                  <div className="file-preview">
                    {mediaPreview.type === 'video' ? <VideoFileIcon /> : <PictureAsPdfIcon />}
                    <span>{mediaPreview.name}</span>
                  </div>
                )}
                <div className="media-info">
                  <span>{mediaPreview.name} ({mediaPreview.size}MB)</span>
                  <button onClick={() => {
                    setMediaFile(null);
                    setMediaPreview(null);
                    setUploadError('');
                  }}>
                    <DeleteIcon /> Remove
                  </button>
                </div>
              </div>
            )}

            {uploadProgress > 0 && (
              <div className="upload-progress">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}
          </div>

          {/* Preview Section */}
          <div className="message-preview">
            <h4>Preview</h4>
            <div className="preview-box">
              <div className="preview-header">
                <img src="/whatsapp-icon.png" alt="WhatsApp" />
                <span>{event?.name}</span>
              </div>
              <div className="preview-content">
                {messageData.message || <em>Your message will appear here...</em>}
                {mediaPreview && (
                  <div className="preview-media">
                    {mediaPreview.type === 'image' ? (
                      <img src={mediaPreview.url} alt="Attachment" />
                    ) : (
                      <div className="preview-file">
                        📎 {mediaPreview.name}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="composer-actions">
        <button
          className="cancel-btn"
          onClick={() => navigate(`/events/${eventId}`)}
        >
          Cancel
        </button>
        <button
          className="send-btn"
          onClick={handleSend}
          disabled={sending || (!messageData.message.trim() && !mediaFile)}
        >
          {sending ? 'Sending...' : (
            <>
              <SendIcon />
              {messageType === 'custom' ?
                `Send to ${filteredGuests.length} Recipients` :
                'Create Rule'
              }
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MessageComposer;